/** Which record a status rule acted on: its routes and rights differ. */
export type StatusRecordKind = "lead" | "project"

export type StatusSuggestionReason =
  | "suggest_mode"
  | "closing_status"
  | "required_information"
  | "prerequisites_missing"
  /** The fields a blocked move waited on are filled; it is an ordinary move now. */
  | "requirements_met"
  | "milestone_blocked"
  | "refused"

/**
 * A move a status rule offers, as the lead and project details payloads
 * carry it. `target` is the entity's own integer status, or null for a flag
 * (a blocked milestone, a stale lead) that moves nothing when accepted.
 */
export interface StatusSuggestion {
  id: string
  rule: string
  target: number | null
  /** A value this build does not know still renders, as a plain move. */
  reason: StatusSuggestionReason | (string & {})
  /** The labels a person reads ("Project type"). */
  missing_fields: string[]
  /** The form fields behind those labels, which a deep link marks. */
  missing_field_names: string[]
  event: string | null
  /** `label` names the document ("PROP-2026-041"); absent on an older backend. */
  source: {
    type: string
    id: string
    label?: string | null
    /** A meeting's `MeetingCategory` (`SITE_VISIT`), else null. */
    category?: string | null
  } | null
  created: string
}

export type StatusTransitionSource =
  | "manual"
  | "automation"
  | "undo"
  | "import"
  | "create"
  | "update"
  | string

export interface StatusTransition {
  id: string
  previous_status: number | null
  status: number
  occurred_at: string
  actor: { id: string; name: string } | null
  source: StatusTransitionSource
  is_automatic: boolean
  rule: string | null
  event: string | null
  undone_at: string | null
  /** Set only on the latest automatic row the record has not moved past. */
  undo_available_until: string | null
  /** The document the rule's event was about ("PROP-2026-041"), when it has one. */
  source_label?: string | null
  source_category?: string | null
}

export const STATUS_REFUSAL_CODES = [
  "not_automatic",
  "already_undone",
  "undo_expired",
  "record_moved",
  "suggestion_closed",
  "suggestion_stale"
] as const

export type StatusRefusalCode = (typeof STATUS_REFUSAL_CODES)[number]

/** A 409: the record's current state answers no, with a code to say why. */
export interface StatusRefusal {
  code: StatusRefusalCode | string
  detail?: string
}

export interface LeadSuggestionAnswer {
  status: number
  status_suggestions: StatusSuggestion[]
}

export interface ProjectSuggestionAnswer {
  project_status: number
  status_suggestions: StatusSuggestion[]
}

export interface LeadUndoAnswer {
  status: number
  status_transitions: StatusTransition[]
}

export interface ProjectUndoAnswer {
  project_status: number
  status_transitions: StatusTransition[]
}

/** What closing a record through a suggestion may need to send. */
export interface StatusSuggestionAcceptBody {
  lost_reason?: string
  lost_notes?: string
  cancel_appointments?: boolean
  void_signature_requests?: boolean
  /** A Won that converts the lead asks for its PM, as the status change does. */
  prompt_project_manager?: boolean
  project_manager_id?: string
}
